import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {CommonModule} from "@angular/common";
import {MatError} from "@angular/material/input";
import {FormControl, FormGroupDirective} from "@angular/forms";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {fromEvent, map, merge, of, startWith, switchMap} from "rxjs";

const ERROR_REQUIRED_KEY = 'required';
const ERROR_REQUIRED_MESSAGE = 'Please enter a value';

@Component({
  selector: 'lib-form-field-errors',
  standalone: true,
  imports: [CommonModule, MatError],
  template: `
    @if (errors().length > 0) {
      @for (error of errors(); track $index) {
        <mat-error
        [ngClass]="touched() ? 'link-error' : 'link-default'">{{ error }}</mat-error>
      }
    }
  `
})
export class FormFieldErrorsComponent {
  controlName = input.required<string>();
  errorMap = input.required<Record<string, Record<string, string>>>();

  private _formGroupDirective = inject(FormGroupDirective);

  private _changeNonce = signal(0);

  private _ctrlChanges = effect((onCleanup) => {
    const ctrl = this._formGroupDirective.control?.get(this.controlName()) as FormControl | null;
    if (!ctrl) return;

    const sub = merge(ctrl.statusChanges, ctrl.valueChanges)
      .subscribe(() => this._changeNonce.update(n => n + 1));

    onCleanup(() => sub.unsubscribe());
  }, {allowSignalWrites: true});

  errors = computed<string[]>(() => {
    this._changeNonce();
    const ctrl = this._formGroupDirective.control?.get(this.controlName()) as FormControl | null;

    if (!ctrl || !ctrl?.errors) return [];

    if (ctrl.hasError(ERROR_REQUIRED_KEY)) {
      return [ERROR_REQUIRED_MESSAGE]
    }

    const fieldName = this.controlName();
    const fieldErrorDefs = this.errorMap()[fieldName];
    if(!fieldErrorDefs) return [];

    return Object.keys(ctrl.errors)
      .filter(ctrlErrorKey => ctrlErrorKey in fieldErrorDefs)
      .map(controlErrorKey => fieldErrorDefs[controlErrorKey])
  });

  private _touched = toSignal(
    toObservable(this.controlName).pipe(
      map(name => this._formGroupDirective.control?.get(name) as FormControl | null),
      switchMap(ctrl => {
        if (!ctrl) return of(false);

        // Get the actual input/textarea/select element in the DOM
        const inputElement = document.querySelector(`[formControlName="${this.controlName()}"]`) as HTMLInputElement | null;

        if (inputElement) {
          return fromEvent(inputElement, 'blur').pipe(
            startWith(null),
            map(() => ctrl.touched || ctrl.dirty)
          );
        }

        return of(false);
      })
    )
  );

  touched = computed(() => this._touched() ?? false);

}
